import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  useNotifications,
  type Notification,
  type NotificationSeverity,
} from "../../lib/notifications";
import { cn } from "../../lib/cn";
import { IconButton } from "./IconButton";
import { AlertTriangle, CheckCircle, X } from "./Icon";

/** Success/info clear themselves; errors and warnings stay until dismissed. */
const AUTO_DISMISS_MS: Partial<Record<NotificationSeverity, number>> = {
  success: 5000,
  info: 6000,
};

const ACCENT: Record<NotificationSeverity, string> = {
  error: "var(--status-error)",
  warning: "var(--status-warning)",
  success: "var(--status-success)",
  info: "var(--status-running)",
};

/**
 * Persistent toast stack, bottom-right, above modals (z-60). Fed by the
 * notification store, so an outcome survives the surface that produced it —
 * close the Done modal and a failed merge is still sitting here.
 */
export function Toasts() {
  const notifications = useNotifications((s) => s.notifications);
  return (
    <div
      className={cn(
        "fixed z-[60] bottom-4 right-4 left-4 sm:left-auto sm:w-[380px]",
        "flex flex-col-reverse gap-2 pointer-events-none",
      )}
      aria-live="polite"
    >
      <AnimatePresence initial={false}>
        {notifications.map((n) => (
          <ToastItem key={n.id} notification={n} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastItem({ notification: n }: { notification: Notification }) {
  const dismiss = useNotifications((s) => s.dismiss);
  const Glyph = n.severity === "success" ? CheckCircle : AlertTriangle;

  useEffect(() => {
    const ms = AUTO_DISMISS_MS[n.severity];
    if (!ms) return;
    const t = setTimeout(() => dismiss(n.id), ms);
    return () => clearTimeout(t);
  }, [n.id, n.severity, dismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, scale: 0.96 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      role={n.severity === "error" ? "alert" : "status"}
      className={cn(
        "pointer-events-auto flex overflow-hidden rounded-md",
        "bg-bg-surface border border-border-subtle shadow-lg",
      )}
    >
      <span
        aria-hidden="true"
        className="w-1 shrink-0"
        style={{ background: ACCENT[n.severity] }}
      />
      <div className="min-w-0 flex-1 p-3">
        <div className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-px shrink-0"
            style={{ color: ACCENT[n.severity] }}
          >
            <Glyph />
          </span>
          <div className="min-w-0 flex-1 text-small text-text-primary break-words">
            {n.title}
          </div>
          <IconButton
            icon={<X />}
            label="Dismiss"
            size="sm"
            onClick={() => dismiss(n.id)}
          />
        </div>
        {n.detail && (
          <pre
            className={cn(
              "mt-2 text-mono text-text-secondary whitespace-pre-wrap break-words",
              "bg-bg-inset border border-border-subtle rounded-md p-2",
              "max-h-[28vh] overflow-auto",
            )}
          >
            {n.detail}
          </pre>
        )}
      </div>
    </motion.div>
  );
}
